import {
	getCurrentScope,
	onScopeDispose,
	readonly,
	type Ref,
	ref,
} from 'vue';

export type GetViewportWatchCallback = (viewport: Viewport) => void;

interface UseViewportReturn {
	fullViewport: Ref<Viewport>;
	visibleViewport: Ref<Viewport>;
	stop: () => void;
}

export interface Viewport {
	height: number;
	offsetTop: number;
	width: number;
}

interface ViewportState {
	fullViewport: Viewport;
	resizeObserver: ResizeObserver;
	visibleViewport: Viewport;
	watchers: Record<ViewportType, GetViewportWatchCallback[]>;
}

export type ViewportType = 'full' | 'visible';

let preventVirtualKeyboardScroll = false;
let preventVirtualKeyboardScrollElement: HTMLElement | null = null;
let state: ViewportState | null = null;

/**
 * Runs a viewport subscriber without allowing one consumer to interrupt the others.
 *
 * @param callback Subscriber callback to invoke.
 */
function callSafe(callback: () => void): void {
	try {
		callback();
	} catch {
		/** Viewport observation must continue when one consumer throws. */
	}
}

/**
 * Coalesces rapid visual-viewport events while retaining an immediate first update.
 *
 * @param callback Viewport synchronization callback.
 * @param wait Minimum delay between trailing invocations in milliseconds.
 * @returns Debounced callback.
 */
function debounce(
	callback: () => void,
	wait: number,
): () => void {
	let timeout: number | undefined;

	return (): void => {
		if (!timeout) {
			callback();
			timeout = window.setTimeout(
				() => {
					timeout = undefined;
				},
				wait,
			);
			return;
		}

		window.clearTimeout(timeout);
		timeout = window.setTimeout(
			() => {
				callback();
				timeout = undefined;
			},
			wait,
		);
	};
}

/**
 * Lazily initializes the shared viewport observers when the first consumer subscribes.
 *
 * @returns Singleton {@link ViewportState} used by every consumer in the current app.
 */
function ensureInitialized(): ViewportState {
	if (state) {
		return state;
	}

	const resizeObserver = new ResizeObserver(() => onViewportChange());
	const initialState: ViewportState = {
		fullViewport: readFullViewport(),
		resizeObserver,
		visibleViewport: readVisibleViewport(),
		watchers: {
			full: [],
			visible: [],
		},
	};

	/**
	 * Reads both viewport contracts and notifies only subscribers whose dimensions changed.
	 */
	function onViewportChange(): void {
		const currentFullViewport = readFullViewport();
		const currentVisibleViewport = readVisibleViewport();

		if (
			currentFullViewport.height !== initialState.fullViewport.height
			|| currentFullViewport.width !== initialState.fullViewport.width
		) {
			initialState.fullViewport = currentFullViewport;

			for (const watcher of initialState.watchers.full) {
				callSafe(() => watcher(currentFullViewport));
			}
		}

		if (
			currentVisibleViewport.height !== initialState.visibleViewport.height
			|| currentVisibleViewport.offsetTop !== initialState.visibleViewport.offsetTop
			|| currentVisibleViewport.width !== initialState.visibleViewport.width
		) {
			initialState.visibleViewport = currentVisibleViewport;

			for (const watcher of initialState.watchers.visible) {
				callSafe(() => watcher(currentVisibleViewport));
			}
		}
	}

	const onViewportChangeDebounce = debounce(
		onViewportChange,
		100,
	);

	resizeObserver.observe(document.documentElement);

	window.visualViewport?.addEventListener(
		'resize',
		onViewportChangeDebounce,
	);
	window.visualViewport?.addEventListener(
		'scroll',
		onViewportChangeDebounce,
	);

	state = initialState;
	return state;
}

/**
 * Prevents the focused host application from scrolling behind the virtual keyboard.
 *
 * @param event Touch movement dispatched within the application root.
 */
function onWebAppScroll(event: TouchEvent): void {
	const watcherState = ensureInitialized();

	if (
		watcherState.visibleViewport.height !== window.innerHeight
		|| watcherState.visibleViewport.width !== window.innerWidth
	) {
		event.preventDefault();
	}
}

/**
 * Reads the layout viewport that remains stable while a virtual keyboard is open.
 *
 * @returns Full {@link Viewport} dimensions in CSS pixels.
 */
function readFullViewport(): Viewport {
	return {
		height: Math.floor(
			Math.max(
				document.documentElement.clientHeight,
				window.innerHeight || 0,
			),
		),
		offsetTop: 0,
		width: Math.floor(
			Math.max(
				document.documentElement.clientWidth,
				window.innerWidth || 0,
			),
		),
	};
}

/**
 * Reads the currently visible area, excluding an open operating-system keyboard.
 *
 * @returns Visible {@link Viewport} dimensions and vertical offset in CSS pixels, or zeroes when unsupported.
 */
function readVisibleViewport(): Viewport {
	return {
		height: Math.floor(window.visualViewport?.height || 0),
		offsetTop: Math.floor(window.visualViewport?.offsetTop || 0),
		width: Math.floor(window.visualViewport?.width || 0),
	};
}

/**
 * Resolves the app root whose touch scrolling should be suppressed while typing.
 *
 * @returns Creator or kiosk root element when it is mounted.
 */
function resolveScrollContainer(): HTMLElement | null {
	return (
		document.getElementById('webapp')
		?? document.getElementById('app')
	);
}

/**
 * Publishes full and visible viewport heights as CSS variables for application layouts.
 */
export function configureAppHeight(): void {
	if (
		document.documentElement.style.getPropertyValue('--app-height')
		&& document.documentElement.style.getPropertyValue('--app-full-height')
	) {
		return;
	}

	/**
	 * Synchronizes the root height variables with the latest observed dimensions.
	 */
	function applyHeights(): void {
		const watcherState = ensureInitialized();

		if (watcherState.visibleViewport.height) {
			document.documentElement.style.setProperty(
				'--app-height',
				`${watcherState.visibleViewport.height}px`,
			);
		} else {
			document.documentElement.style.removeProperty('--app-height');
		}

		if (watcherState.fullViewport.height) {
			document.documentElement.style.setProperty(
				'--app-full-height',
				`${watcherState.fullViewport.height}px`,
			);
		} else {
			document.documentElement.style.removeProperty('--app-full-height');
		}
	}

	applyHeights();
	watch(
		applyHeights,
		[
			'full',
			'visible',
		],
	);
}

/**
 * Returns the stable layout viewport, including the area covered by a virtual keyboard.
 *
 * @returns Full {@link Viewport} dimensions.
 */
export function getFullViewport(): Viewport {
	return ensureInitialized().fullViewport;
}

/**
 * Returns the currently visible viewport, excluding an open virtual keyboard.
 *
 * @returns Visible {@link Viewport} dimensions.
 */
export function getVisibleViewport(): Viewport {
	return ensureInitialized().visibleViewport;
}

/**
 * Enables or disables touch-scroll suppression while a virtual keyboard covers the app.
 *
 * Creator uses `#webapp`, while standalone consumers such as kiosk use `#app`.
 *
 * @param prevent Whether virtual-keyboard touch movement should be prevented.
 */
export function preventScrollOnVirtualKeyboard(prevent = true): void {
	const scrollContainer = resolveScrollContainer();

	if (
		preventVirtualKeyboardScroll === prevent
		&& (
			!prevent
			|| preventVirtualKeyboardScrollElement === scrollContainer
		)
	) {
		return;
	}

	preventVirtualKeyboardScrollElement?.removeEventListener(
		'touchmove',
		onWebAppScroll,
	);
	preventVirtualKeyboardScroll = prevent;
	preventVirtualKeyboardScrollElement = prevent
		? scrollContainer
		: null;

	preventVirtualKeyboardScrollElement?.addEventListener(
		'touchmove',
		onWebAppScroll,
		{
			passive: false,
		},
	);
}

/**
 * Exposes reactive full and visible viewport dimensions within a Vue scope.
 *
 * @returns Reactive viewport references and an explicit cleanup callback.
 */
export function useViewport(): UseViewportReturn {
	const watcherState = ensureInitialized();
	const fullViewportState = ref(watcherState.fullViewport);
	const visibleViewportState = ref(watcherState.visibleViewport);
	const disposers: Array<() => void> = [
		watch((value) => {
			fullViewportState.value = value;
		}),
		watch(
			(value) => {
				visibleViewportState.value = value;
			},
			'visible',
		),
	];

	/**
	 * Removes the subscriptions owned by this composable instance.
	 */
	function stop(): void {
		while (disposers.length) {
			const disposer = disposers.pop();
			disposer?.();
		}
	}

	if (getCurrentScope()) {
		onScopeDispose(stop);
	}

	return {
		fullViewport: readonly(fullViewportState),
		stop,
		visibleViewport: readonly(visibleViewportState),
	};
}

/**
 * Subscribes to full, visible, or both viewport dimension contracts.
 *
 * @param callback Callback receiving the changed {@link Viewport}.
 * @param type Viewport contracts that should trigger the callback.
 * @returns Cleanup callback that removes this subscription.
 */
export function watch(
	callback: GetViewportWatchCallback,
	type: ViewportType[] | ViewportType = 'full',
): () => void {
	const watcherState = ensureInitialized();
	const arrayType = (
		Array.isArray(type)
			? type
			: [type]
	);

	for (const singleType of arrayType) {
		watcherState.watchers[singleType].push(callback);
	}

	return (): void => {
		for (const singleType of arrayType) {
			const index = watcherState.watchers[singleType].indexOf(callback);

			if (index > -1) {
				watcherState.watchers[singleType].splice(
					index,
					1,
				);
			}
		}
	};
}
